<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps({
  lines: { type: Number, default: 6 },
  // Reset expansion when the displayed message changes, keeping state local.
  contentKey: { type: [String, Number], default: "" },
});
const content = ref(null);
const expanded = ref(false);
const overflowing = ref(false);
const limit = ref(126);
const collapsed = computed(() => overflowing.value && !expanded.value);
let observer;
let disposed = false;

const measure = () => {
  if (disposed || !content.value) return;
  const lineHeight = parseFloat(getComputedStyle(content.value).lineHeight) || 21;
  // Keep the slot's outer paragraph margins without clipping half a line.
  const first = content.value.firstElementChild;
  const top = first ? parseFloat(getComputedStyle(first).marginTop) || 0 : 0;
  limit.value = lineHeight * Math.max(1, props.lines) + top;
  overflowing.value = content.value.getBoundingClientRect().height > limit.value + 1;
};
watch(() => props.contentKey, async () => {
  expanded.value = false;
  await nextTick();
  measure();
});
watch(() => props.lines, async () => {
  await nextTick();
  measure();
});
onMounted(() => {
  measure();
  // Observe the natural content height, including asynchronous rich text rendering.
  observer = new ResizeObserver(measure);
  observer.observe(content.value);
});
onBeforeUnmount(() => {
  disposed = true;
  observer?.disconnect();
});
</script>

<template>
  <div class="shared-expandable-content">
    <div
      class="shared-expandable-content__viewport"
      :class="{ 'shared-expandable-content__viewport--collapsed': collapsed }"
      :style="collapsed ? { maxHeight: limit + 'px' } : undefined"
      @focusin="expanded = true"
    >
      <div ref="content" class="shared-expandable-content__body"><slot /></div>
    </div>
    <button
      v-if="overflowing"
      type="button"
      class="shared-expandable-content__toggle"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >{{ expanded ? "Leer menos" : "Leer más" }}</button>
  </div>
</template>

<style scoped>
.shared-expandable-content { min-width: 0; overflow-anchor: none; }
.shared-expandable-content__viewport--collapsed { overflow: hidden; overflow-anchor: none; }
.shared-expandable-content__body { display: flow-root; }
.shared-expandable-content__toggle {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 4px 0;
  border: 0;
  background: transparent;
  color: #474ce6;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
.shared-expandable-content__toggle:hover { text-decoration: underline; }
.shared-expandable-content__toggle:focus-visible {
  outline: 2px solid #409eff;
  outline-offset: 2px;
  border-radius: 4px;
}
@media (max-width: 767px) {
  .shared-expandable-content__toggle { min-height: 44px; }
}
</style>
